import { Box, Card, Container, Heading, Text } from '@nrx/ui';
import type { JSX } from 'react';

const MAIN_SITE_URL = 'https://www.nourishedrx.com';

/**
 * Hero image from Notion Brand Assets (vibrant food flat lay), as WebP at two widths.
 *
 * It arrived as a single 5.1 MB, 2004x1268 uncompressed PNG, and NOTHING in the pipeline would
 * have caught that: `pack-web-app`'s size gate sums only Brotli-compressed initial `.js`/`.css`
 * under `dist/<project>/assets`, while `public/` is copied verbatim. So the whole 5.1 MB was the
 * `loading="eager"` hero — a multi-second blank panel for the ACCESS/Medicare population on mobile
 * data. `apps/consult/src/hero-asset-budget.spec.ts` now fails on any oversized asset under
 * `public/`, so the next one is caught by a test rather than by a reviewer.
 *
 * Two widths with a `sizes` hint rather than one compromise file: the container caps at
 * `--container-4` (1136px), so 1200w serves 1x fully and mobile pulls ~162 KB instead of 5.1 MB,
 * while 2x desktop gets 1600w. The image is DECORATIVE (`alt=""`, and a scrim covers its lower
 * half), which is what makes q78 the right trade rather than a visible one.
 */
const HERO_IMAGE = '/images/welcome-hero.1600.webp';
const HERO_SRCSET = '/images/welcome-hero.1200.webp 1200w, /images/welcome-hero.1600.webp 1600w';
/** Mirrors `Container size="4"` (`--container-4: 1136px`); below that the card is full-bleed. */
const HERO_SIZES = '(min-width: 1136px) 1136px, 100vw';

/**
 * Static "Welcome aboard" landing page at /welcome.
 * Hero-style layout featuring brand imagery with overlaid headline and CTA.
 *
 * @remarks The brand header and footer come from `ConsultLayout`, which wraps the whole route
 * tree in `app.tsx` — this page renders content only.
 *
 * The CTA is a bare `<a>` on an app-scoped class, not `Link` or `Button`. `Button` renders a
 * `<button>`, which would break `getByRole('link', { name: /learn more/i })` and, more to the
 * point, would be the wrong element for a cross-origin navigation; `Link` is body-text styling and
 * giving it a pill fill through `className` is the W3 violation. `@nrx/ui` has no
 * anchor-rendered CTA, so this is a genuine inventory gap rather than a bespoke reimplementation
 * of something that exists — worth raising upstream.
 *
 * Both overlay strings stay inside one paragraph each: `getByText` matches an element's DIRECT
 * text nodes, and the welcome spec looks for "we're glad you're here" and
 * "food is health, food is care" in the same paragraph.
 */
export default function WelcomePage(): JSX.Element {
  return (
    <Container as="section" size="4" pt="8" pb="10">
      <Card elevation="pop" className="consult-welcome">
        <Box className="consult-welcome__hero">
          {/*
            `loading="eager"` is correct and deliberate: this is the LCP element, so lazy-loading it
            would delay the paint it defines. The fix for the slow hero was the 13x smaller payload,
            not deferring it. `width`/`height` are the encoder's actual output (1600x1013), so the
            intrinsic ratio is exact and the panel reserves its space before the image arrives.
          */}
          <img
            src={HERO_IMAGE}
            srcSet={HERO_SRCSET}
            sizes={HERO_SIZES}
            width={1600}
            height={1013}
            alt=""
            className="consult-welcome__image"
            loading="eager"
          />
          {/* Forest scrim over the lower half so the overlaid text stays legible on any image. */}
          <div className="consult-welcome__scrim" aria-hidden />
          <Box className="consult-welcome__overlay">
            <Heading as="h1" size="9" className="consult-welcome__headline">
              <span className="consult-on-scrim">Welcome aboard</span>
            </Heading>
            <Text as="p" size="5" className="consult-welcome__lede">
              <span className="consult-on-scrim">
                We&apos;re glad you&apos;re here. Food is health, food is care—and we&apos;re here
                to support you every step of the way.
              </span>
            </Text>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="consult-welcome__cta"
            >
              Learn more
            </a>
          </Box>
        </Box>
        <Box className="consult-welcome__tagline">
          <Text as="p" color="muted">
            Connecting the worlds of food, health, and care.
          </Text>
        </Box>
      </Card>
    </Container>
  );
}
